import { useState, useRef, useEffect, useMemo } from 'react'
import { useNavigate, NavLink, useLocation } from 'react-router-dom'
import { Menu, LogOut, Settings, ChevronDown, Home } from 'lucide-react'
import { ThemeToggle } from '../ThemeToggle'

interface TopBarProps {
  onMenuClick: () => void
  sidebarOpen?: boolean
}

interface BreadcrumbItem {
  label: string
  href?: string
}

// Generate breadcrumbs based on current route
function generateBreadcrumbs(pathname: string): BreadcrumbItem[] {
  const routeMap: Record<string, BreadcrumbItem[]> = {
    '/dashboard': [{ label: 'Dashboard' }],
    '/users': [{ label: 'Users' }],
    '/settings': [{ label: 'Settings' }],
    '/company/profile': [{ label: 'Company Profile' }],
    '/companies': [{ label: 'Companies' }],
    '/content': [{ label: 'Content Management' }],
    '/library': [{ label: 'Content Library' }],
    '/presentations': [{ label: 'Presentations' }],
    '/chat': [{ label: 'AI Assistant' }],
  }

  // Exact match
  if (routeMap[pathname]) {
    return routeMap[pathname]
  }

  // Dynamic routes
  if (/^\/presentations\/[\w-]+$/.test(pathname) || /^\/presentations\/viewer\/[\w-]+$/.test(pathname)) {
    return [{ label: 'Presentations', href: '/presentations' }, { label: 'View Presentation' }]
  }
  if (pathname === '/presentations/new') {
    return [{ label: 'Presentations', href: '/presentations' }, { label: 'New Presentation' }]
  }
  if (/^\/presentations\/[\w-]+\/edit$/.test(pathname)) {
    return [{ label: 'Presentations', href: '/presentations' }, { label: 'Edit Presentation' }]
  }
  if (pathname === '/companies/create') {
    return [{ label: 'Companies', href: '/companies' }, { label: 'Create Company' }]
  }
  if (/^\/companies\/[\w-]+$/.test(pathname)) {
    return [{ label: 'Companies', href: '/companies' }, { label: 'Company Details' }]
  }
  if (/^\/companies\/[\w-]+\/edit$/.test(pathname)) {
    return [{ label: 'Companies', href: '/companies' }, { label: 'Edit Company' }]
  }

  // Fallback
  return [{ label: 'Dashboard', href: '/dashboard' }]
}

export function TopBar({ onMenuClick }: TopBarProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const [user, setUser] = useState<{ name: string; email: string; role: string } | null>(null)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Generate breadcrumbs from current route
  const breadcrumbs = useMemo(() => generateBreadcrumbs(location.pathname), [location.pathname])

  useEffect(() => {
    const userStr = localStorage.getItem('user')
    if (userStr) {
      try {
        setUser(JSON.parse(userStr))
      } catch (error) {
        console.error('Failed to parse user data:', error)
      }
    }
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleLogout = async () => {
    try {
      await fetch('http://localhost:3000/api/auth/logout', {
        method: 'POST',
        credentials: 'include'
      })
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      navigate('/login')
    }
  }

  return (
    <header className="sticky top-0 h-16 bg-card border-b border-border z-60">
      <div className="flex items-center justify-between h-full px-4 sm:px-6">
        {/* Left: Menu button and breadcrumbs */}
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <button
            onClick={onMenuClick}
            className="lg:hidden p-2 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
            aria-label="Toggle sidebar"
          >
            <Menu className="w-5 h-5" />
          </button>

          {/* Dynamic Breadcrumbs */}
          <nav className="hidden sm:flex items-center gap-2 text-sm min-w-0">
            <NavLink
              to="/dashboard"
              className="hover:text-foreground transition-colors text-muted-foreground flex-shrink-0"
              aria-label="Dashboard"
            >
              <Home className="w-4 h-4" />
            </NavLink>

            {breadcrumbs.map((item, index) => (
              <div key={index} className="flex items-center gap-2 min-w-0">
                <span className="text-muted-foreground flex-shrink-0">/</span>
                {item.href ? (
                  <NavLink
                    to={item.href}
                    className={({ isActive }) => `
                      hover:text-foreground transition-colors truncate
                      ${isActive ? 'text-foreground font-medium' : 'text-muted-foreground'}
                    `}
                  >
                    {item.label}
                  </NavLink>
                ) : (
                  <span className="text-foreground font-medium truncate">{item.label}</span>
                )}
              </div>
            ))}
          </nav>

          {/* Mobile: Show just current page title */}
          <span className="sm:hidden text-sm font-medium text-foreground truncate">
            {breadcrumbs[breadcrumbs.length - 1]?.label || 'Dashboard'}
          </span>
        </div>

        {/* Right: Theme toggle and user dropdown */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Theme Toggle */}
          <ThemeToggle />

          {/* User dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted transition-colors"
              aria-label="User menu"
            >
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center text-white text-sm font-medium">
                {user?.name?.charAt(0).toUpperCase() || user?.email.charAt(0).toUpperCase() || 'U'}
              </div>
              <div className="hidden md:block text-left">
                <p className="text-sm font-medium text-foreground">
                  {user?.name || 'User'}
                </p>
                <p className="text-xs text-muted-foreground capitalize">
                  {user?.role.replace('_', ' ') || ''}
                </p>
              </div>
              <ChevronDown
                className={`w-4 h-4 text-muted-foreground transition-transform ${
                  dropdownOpen ? 'rotate-180' : ''
                }`}
              />
            </button>

            {/* Dropdown menu */}
            {dropdownOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-card border border-border rounded-lg shadow-lg py-1 z-dropdown-content">
                <div className="px-4 py-3 border-b border-border">
                  <p className="text-sm font-medium text-foreground">{user?.name || 'User'}</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                </div>

                <div className="py-1">
                  <button
                    onClick={() => {
                      navigate('/settings')
                      setDropdownOpen(false)
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors text-left"
                  >
                    <Settings className="w-4 h-4" />
                    Settings
                  </button>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-destructive hover:bg-destructive/10 transition-colors text-left"
                  >
                    <LogOut className="w-4 h-4" />
                    Logout
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  )
}
